// MARK://dhk8/admin-shell/pages/modules/RecipeDetailPage
// Detalle de receta → dhk8-catalog-recipes (en prod). Portado de dhk8-admin
// `/recetario/recetas/:id` (8 tabs) al sistema HIG-green de admin-shell —
// ingredientes (con costeo), alérgenos, KDS, datos de preparación.
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Plus, Trash2, CheckCircle2, EyeOff, Archive, Copy, Pencil, Calculator } from 'lucide-react'
import * as rec from '../../lib/clients/recipes-catalog.js'
import * as cat from '../../lib/clients/catalog.js'
import type { Product } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'
import FieldHelp from '../../components/FieldHelp.js'
import UnitPicker, { unitShort } from '../../components/UnitPicker.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import EntityCombobox from '../../components/EntityCombobox.js'
import InlineEdit from '../../components/InlineEdit.js'
import AuditTrail from '../../components/AuditTrail.js'

const STATUS_TONE: Record<rec.RecipeStatus, StatusTone> = {
  draft: 'warn', published: 'ok', archived: 'neutral',
}
function mxn(cents: number | null): string {
  if (cents == null) return '—'
  return (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

type Tab = 'ingredientes' | 'costeo' | 'alergenos' | 'kds' | 'historial'

export default function RecipeDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const recipe = useAsync((s) => rec.getRecipe(id, s), [id])
  // El selector de "Agregar ingrediente" ofrece TODO lo que sirve como componente
  // de una receta: insumos (INGREDIENT) Y productos (PRODUCT/COMBO/SERVICE), menos
  // modificadores. Antes solo ofrecía INGREDIENT; como SAVIs no tiene insumos dados
  // de alta, el selector salía vacío y no se podía crear la receta. `listAllProducts`
  // (grafo erp-products, tenant del JWT) cubre ambos y también resuelve el nombre de
  // cada `productId` referenciado + el tag `needs-attention:cost-0`.
  const pickable = useAsync((s) => cat.listAllProducts(s))
  // Sub-recetas: una receta hija puede usarse como componente de otra (ej. Latte
  // usa Espresso). El selector ofrece todas las recetas menos esta misma.
  const allRecipes = useAsync((s) => rec.listRecipes(s))

  const [tab, setTab] = useState<Tab>('ingredientes')
  const [busy, setBusy] = useState(false)

  // add-ingredient sheet (wasteFactor = % merma como decimal; optional = ingrediente opcional)
  const [addingIng, setAddingIng] = useState(false)
  const [ingForm, setIngForm] = useState({ productId: '', quantity: 1, unit: 'g', wasteFactor: 0, optional: false })
  const [ingError, setIngError] = useState<string | null>(null)
  // crear-insumo-al-vuelo (fix #2): da de alta un INGREDIENT desde la receta y lo
  // preselecciona, sin tener que salir a otra pantalla.
  const [showNewInsumo, setShowNewInsumo] = useState(false)
  const [newInsumo, setNewInsumo] = useState({ name: '', unit: 'g', costPesos: '' })
  const [newInsumoErr, setNewInsumoErr] = useState<string | null>(null)
  const [creatingInsumo, setCreatingInsumo] = useState(false)

  // edit-ingredient sheet (qty + unit)
  const [editingIng, setEditingIng] = useState<rec.RecipeIngredient | null>(null)
  const [editIngForm, setEditIngForm] = useState({ quantity: 1, unit: 'g', wasteFactor: 0, optional: false })
  const [editIngError, setEditIngError] = useState<string | null>(null)

  // add-sub-recipe sheet (subId = receta hija; quantity = cuántas veces se usa)
  const [addingSub, setAddingSub] = useState(false)
  const [subForm, setSubForm] = useState({ subId: '', quantity: 1 })
  const [subError, setSubError] = useState<string | null>(null)

  // rename-recipe sheet
  const [renaming, setRenaming] = useState(false)
  const [renameName, setRenameName] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)

  const r = recipe.data
  // Todo lo que se puede usar como componente de una receta (insumos + productos),
  // agrupado para el selector y para resolver nombres por productId.
  const pk = pickable.data ?? []
  const pickableGroups = [
    { label: 'Insumos', items: pk.filter((p) => p.type === 'INGREDIENT') },
    { label: 'Productos', items: pk.filter((p) => p.type === 'PRODUCT' || !p.type) },
    { label: 'Otros', items: pk.filter((p) => p.type === 'COMBO' || p.type === 'SERVICE') },
  ].filter((g) => g.items.length > 0)
  /** Resuelve el nombre legible de un productId; degrada a "(insumo no encontrado)"
   *  en vez de filtrar el UUID. */
  const productLookup = (pid: string) => pk.find((p) => p.id === pid) ?? null
  const productName = (pid: string) => productLookup(pid)?.name ?? '(insumo no encontrado)'
  const productNeedsAttention = (pid: string) =>
    (productLookup(pid)?.tags ?? []).includes('needs-attention:cost-0')

  // EntityCombobox: busca sobre el catálogo ya cargado (insumos + productos), sin
  // modificadores (igual que pickableGroups). Devuelve hasta 50 coincidencias.
  async function searchPickable(query: string): Promise<Product[]> {
    const term = query.trim().toLowerCase()
    const base = pickableGroups.flatMap((g) => g.items)
    const matched = term ? base.filter((p) => p.name.toLowerCase().includes(term) || (p.sku ?? '').toLowerCase().includes(term)) : base
    return matched.slice(0, 50)
  }
  const ingComboValue = ingForm.productId ? (productLookup(ingForm.productId) ?? null) : null

  // Guarda la cantidad de un ingrediente in-place (InlineEdit en la fila), sin abrir
  // el sheet de edición; conserva unidad/merma/opcional.
  async function saveIngredientQtyInline(ing: rec.RecipeIngredient, quantity: number) {
    if (!r) return
    if (!(quantity > 0)) throw new Error('La cantidad debe ser mayor a cero.')
    if (quantity === Number(ing.quantity)) return
    await rec.updateIngredientQty(r.id, ing.id, { quantity, unit: ing.unit, wasteFactor: Number(ing.wasteFactor) || 0, optional: ing.optional })
    recipe.reload()
  }

  async function act(fn: () => Promise<unknown>, errMsg: string) {
    setBusy(true)
    try { await fn(); recipe.reload() }
    catch (e) { alert(e instanceof Error ? e.message : errMsg) }
    finally { setBusy(false) }
  }

  async function saveIngredient() {
    if (!r) return
    setIngError(null)
    if (!ingForm.productId) { setIngError('Selecciona un producto.'); return }
    if (ingForm.quantity <= 0) { setIngError('La cantidad debe ser mayor a cero.'); return }
    setBusy(true)
    try {
      await rec.addIngredient(r.id, ingForm)
      setAddingIng(false); setIngForm({ productId: '', quantity: 1, unit: 'g', wasteFactor: 0, optional: false })
      recipe.reload()
    } catch (e) {
      setIngError(e instanceof Error ? e.message : 'No se pudo agregar el ingrediente.')
    } finally { setBusy(false) }
  }

  async function createNewInsumo() {
    setNewInsumoErr(null)
    if (!newInsumo.name.trim()) { setNewInsumoErr('El nombre del insumo es obligatorio.'); return }
    setCreatingInsumo(true)
    try {
      const costCents = Math.max(0, Math.round((Number.parseFloat(newInsumo.costPesos) || 0) * 100))
      const created = await cat.createIngredient({ name: newInsumo.name.trim(), unit: newInsumo.unit.trim() || 'g', costCents })
      pickable.reload() // recarga la lista para que el nuevo insumo aparezca en el selector
      setIngForm((f) => ({ ...f, productId: created.id, unit: created.unit || newInsumo.unit.trim() || 'g' }))
      setShowNewInsumo(false)
      setNewInsumo({ name: '', unit: 'g', costPesos: '' })
    } catch (e) {
      setNewInsumoErr(e instanceof Error ? e.message : 'No se pudo crear el insumo.')
    } finally { setCreatingInsumo(false) }
  }

  function openEditIngredient(ing: rec.RecipeIngredient) {
    setEditIngForm({ quantity: Number(ing.quantity) || 0, unit: ing.unit, wasteFactor: Number(ing.wasteFactor) || 0, optional: ing.optional })
    setEditIngError(null)
    setEditingIng(ing)
  }

  async function saveEditIngredient() {
    if (!r || !editingIng) return
    setEditIngError(null)
    if (editIngForm.quantity <= 0) { setEditIngError('La cantidad debe ser mayor a cero.'); return }
    if (!editIngForm.unit.trim()) { setEditIngError('La unidad es obligatoria.'); return }
    setBusy(true)
    try {
      await rec.updateIngredientQty(r.id, editingIng.id, { quantity: editIngForm.quantity, unit: editIngForm.unit.trim(), wasteFactor: editIngForm.wasteFactor, optional: editIngForm.optional })
      setEditingIng(null)
      recipe.reload()
    } catch (e) {
      setEditIngError(e instanceof Error ? e.message : 'No se pudo actualizar el ingrediente.')
    } finally { setBusy(false) }
  }

  function openRename() {
    if (!r) return
    setRenameName(r.name)
    setRenameError(null)
    setRenaming(true)
  }

  async function saveRename() {
    if (!r) return
    setRenameError(null)
    if (!renameName.trim()) { setRenameError('El nombre es obligatorio.'); return }
    setBusy(true)
    try {
      await rec.updateRecipe(r.id, { name: renameName.trim() })
      setRenaming(false)
      recipe.reload()
    } catch (e) {
      setRenameError(e instanceof Error ? e.message : 'No se pudo renombrar la receta.')
    } finally { setBusy(false) }
  }

  async function saveSubRecipe() {
    if (!r) return
    setSubError(null)
    if (!subForm.subId) { setSubError('Selecciona una receta.'); return }
    if (subForm.subId === r.id) { setSubError('Una receta no puede usarse a sí misma como sub-receta.'); return }
    if (subForm.quantity <= 0) { setSubError('La cantidad debe ser mayor a cero.'); return }
    setBusy(true)
    try {
      await rec.linkSubRecipe(r.id, { subId: subForm.subId, quantity: subForm.quantity })
      setAddingSub(false); setSubForm({ subId: '', quantity: 1 })
      recipe.reload()
    } catch (e) {
      setSubError(e instanceof Error ? e.message : 'No se pudo agregar la sub-receta.')
    } finally { setBusy(false) }
  }

  return (
    <div className="adm-page">
      <div style={{ marginBottom: 12 }}>
        <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/recipes')} aria-label="Volver"><ChevronLeft size={18} /> Recetas</button>
      </div>

      {asyncState(recipe.loading, recipe.error, recipe.reload)}

      {r && (
        <EntityDetailPage
          title={r.name}
          subtitle="Recetario · escandallo y costeo"
          image={r.imageUrl}
          pills={<StatusPill tone={STATUS_TONE[r.status]} label={rec.recipeStatusLabel(r.status)} />}
          kpis={<SummaryStrip items={[
            { label: 'Costo total', value: r.costMissing > 0 ? `${mxn(r.cost)} ⚠` : mxn(r.cost) },
            { label: 'Costo / porción', value: r.yieldServings && r.cost != null ? mxn(Math.round(r.cost / r.yieldServings)) : '—' },
            { label: 'Rendimiento', value: r.yieldServings != null ? `${r.yieldServings} porc.` : '—' },
            { label: 'Tiempo prep.', value: r.prepTimeMin != null ? `${r.prepTimeMin} min` : '—' },
            { label: 'Ingredientes', value: r.ingredients.length },
          ]} />}
          actions={<>
            <button className="adm-btn adm-btn--primary" disabled={busy} onClick={() => act(() => rec.recalcRecipeCost(r.id), 'No se pudo recalcular el costo.')}><Calculator size={15} /> Recalcular costo</button>
            <button className="adm-btn" disabled={busy} onClick={openRename}><Pencil size={15} /> Renombrar</button>
            {r.status === 'draft' && <button className="adm-btn" disabled={busy} onClick={() => act(() => rec.publishRecipe(r.id), 'No se pudo publicar.')}><CheckCircle2 size={15} /> Publicar</button>}
            {r.status === 'published' && <button className="adm-btn" disabled={busy} onClick={() => act(() => rec.unpublishRecipe(r.id), 'No se pudo despublicar.')}><EyeOff size={15} /> Despublicar</button>}
            {r.status === 'published' && <button className="adm-btn" disabled={busy} onClick={() => act(() => rec.archiveRecipe(r.id), 'No se pudo archivar.')}><Archive size={15} /> Archivar</button>}
            <button className="adm-btn" disabled={busy} onClick={() => void rec.duplicateRecipe(r.id).then((d) => navigate(`/recipes/${d.id}`)).catch((e) => alert(e instanceof Error ? e.message : 'Error'))}><Copy size={15} /> Duplicar</button>
          </>}
          tabs={
            <div className="adm-tabs" role="tablist" style={{ display: 'flex', gap: 4 }}>
              {(['ingredientes', 'costeo', 'alergenos', 'kds', 'historial'] as Tab[]).map((t) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={tab === t}
                  className={`adm-btn adm-btn--sm${tab === t ? ' adm-btn--primary' : ' adm-btn--ghost'}`}
                  onClick={() => setTab(t)}
                >
                  {t === 'ingredientes' ? 'Ingredientes' : t === 'costeo' ? 'Costeo' : t === 'alergenos' ? 'Alérgenos' : t === 'kds' ? 'KDS' : 'Historial'}
                </button>
              ))}
            </div>
          }
        >
          {tab === 'ingredientes' && (
            <div className="adm-card adm-card--pad0">
              <div className="adm-list">
                {r.ingredients.length === 0 && <div className="adm-empty">Sin ingredientes. Agrega los componentes para que el backend calcule el costo.</div>}
                {r.ingredients.map((ing) => {
                  const needs = productNeedsAttention(ing.productId)
                  return (
                    <div key={ing.id} className="adm-row">
                      <div className="adm-row__main">
                        <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {productName(ing.productId)}
                          {needs && <span className="adm-badge adm-badge--warn" title="Insumo sin costo (#DIV/0! en el Excel) — asigna un precio antes de costear">Necesita atención · sin costo</span>}
                        </div>
                        <div className="adm-row__meta" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <InlineEdit
                            value={Number(ing.quantity)}
                            type="number"
                            onSave={(v) => saveIngredientQtyInline(ing, Number(v))}
                          />
                          <span>{unitShort(ing.unit)}{Number(ing.wasteFactor) > 0 ? ` · merma ${ing.wasteFactor}%` : ''}{ing.optional ? ' · opcional' : ''}</span>
                        </div>
                      </div>
                      <div className="adm-row__tail">
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy} onClick={() => openEditIngredient(ing)} aria-label="Editar ingrediente"><Pencil size={15} /></button>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={() => act(() => rec.removeIngredient(r.id, ing.id), 'No se pudo quitar.')} aria-label="Quitar"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div style={{ padding: 12, borderTop: '1px solid var(--pos-separator)' }}>
                <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => { setIngForm({ productId: '', quantity: 1, unit: 'g', wasteFactor: 0, optional: false }); setIngError(null); setAddingIng(true) }}><Plus size={15} /> Agregar ingrediente</button>
              </div>
            </div>
          )}

          {/* ── Sub-recetas ──────────────────────────────────────────────── */}
          {/* Una receta puede usar otra receta como componente (ej. Latte usa
              Espresso). linkSubRecipe / unlinkSubRecipe en catalog-recipes. */}
          {tab === 'ingredientes' && (
            <div className="adm-card adm-card--pad0" style={{ marginTop: 16 }}>
              <div style={{ padding: '12px 12px 0' }}><SectionTitle>Sub-recetas</SectionTitle></div>
              <div className="adm-list">
                {r.subRecipes.length === 0 && <div className="adm-empty">Sin sub-recetas. Vincula otra receta (ej. un Espresso) como componente de esta.</div>}
                {r.subRecipes.map((sub) => (
                  <div key={sub.subId} className="adm-row">
                    <div className="adm-row__main">
                      <div className="adm-row__title">{sub.name}</div>
                      <div className="adm-row__meta">Cantidad: {sub.quantity}</div>
                    </div>
                    <div className="adm-row__tail">
                      <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={() => act(() => rec.unlinkSubRecipe(r.id, sub.subId), 'No se pudo quitar la sub-receta.')} aria-label="Quitar sub-receta"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding: 12, borderTop: '1px solid var(--pos-separator)' }}>
                <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => { setSubForm({ subId: '', quantity: 1 }); setSubError(null); setAddingSub(true) }}><Plus size={15} /> Agregar sub-receta</button>
              </div>
            </div>
          )}

          {tab === 'costeo' && (
            <div className="adm-card adm-card--pad">
              <div style={{ fontSize: 13, color: 'var(--pos-fg-tertiary)', marginBottom: 8 }}>Costo total de la receta</div>
              <div style={{ fontSize: 36, fontWeight: 600 }}>{mxn(r.cost)}</div>
              {r.costMissing > 0 && (
                <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'var(--pos-warn-bg, #fef3c7)', color: 'var(--pos-warn-fg, #92400e)', fontSize: 13, fontWeight: 500 }}>
                  ⚠ Costo incompleto · {r.costMissing} insumo{r.costMissing === 1 ? '' : 's'} sin costo. El total mostrado es menor al real hasta capturar el costo de esos insumos.
                </div>
              )}
              <p className="adm-row__meta" style={{ marginTop: 12 }}>
                El costo se recalcula en el backend al cambiar ingredientes. Costo por porción:
                {' '}<strong>{r.yieldServings && r.cost != null ? mxn(Math.round(r.cost / r.yieldServings)) : '—'}</strong>.
              </p>
            </div>
          )}

          {tab === 'alergenos' && (
            <div className="adm-card adm-card--pad">
              <div style={{ fontSize: 13, color: 'var(--pos-fg-tertiary)', marginBottom: 10 }}>Alérgenos presentes</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {rec.ALLERGENS_CATALOG.map((a) => {
                  const active = r.allergens.includes(a)
                  return (
                    <button
                      key={a}
                      type="button"
                      disabled={busy}
                      className={`adm-btn adm-btn--sm${active ? ' adm-btn--primary' : ' adm-btn--ghost'}`}
                      onClick={() => act(() => rec.setAllergens(r.id, active ? r.allergens.filter((x) => x !== a) : [...r.allergens, a]), 'No se pudo actualizar.')}
                    >{a}</button>
                  )
                })}
              </div>
            </div>
          )}

          {tab === 'kds' && (
            <div className="adm-card adm-card--pad" style={{ maxWidth: 480, display: 'grid', gap: 20 }}>
              {/* ── Cocina / KDS ─────────────────────────────────────────── */}
              <section style={{ display: 'grid', gap: 12 }}>
                <SectionTitle>Cocina / KDS</SectionTitle>
                <label className="adm-field">
                  <span className="adm-field__label">Estación de cocina</span>
                  <select
                    className="adm-select"
                    value={r.kdsStation ?? ''}
                    disabled={busy}
                    onChange={(e) => act(() => rec.setStation(r.id, e.target.value), 'No se pudo asignar.')}
                    aria-label="Estación de cocina"
                  >
                    <option value="">— Sin asignar —</option>
                    {rec.KDS_STATIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </label>
                <label className="adm-field">
                  <span className="adm-field__label">Tiempo de preparación (min)</span>
                  <input
                    className="adm-input"
                    type="number"
                    min={0}
                    defaultValue={r.prepTimeMin ?? 0}
                    disabled={busy}
                    onBlur={(e) => {
                      const v = Number(e.target.value)
                      if (!Number.isNaN(v) && v !== (r.prepTimeMin ?? 0)) void act(() => rec.setPrepTime(r.id, v), 'No se pudo guardar.')
                    }}
                  />
                </label>
                <label className="adm-field">
                  <FieldHelp entity="Recipe" field="yield" label="Rendimiento (porciones)" />
                  <input
                    className="adm-input"
                    type="number"
                    min={1}
                    defaultValue={r.yieldServings ?? 1}
                    disabled={busy}
                    onBlur={(e) => {
                      const v = Number(e.target.value)
                      if (!Number.isNaN(v) && v > 0 && v !== (r.yieldServings ?? 0)) void act(() => rec.setYield(r.id, v), 'No se pudo guardar.')
                    }}
                  />
                </label>
                {/* B / CANON §1.7 — rendimiento canónico (cantidad + clave SAT). Coexiste
                    con yieldServings legacy; updateRecipe acepta yieldQuantity/yieldUnit. */}
                <label className="adm-field">
                  <span className="adm-field__label">Rendimiento (cantidad)</span>
                  <input
                    className="adm-input"
                    type="number"
                    min={0}
                    step="any"
                    defaultValue={r.yieldQuantity ?? 1}
                    disabled={busy}
                    onBlur={(e) => {
                      const v = Number(e.target.value)
                      if (!Number.isNaN(v) && v > 0 && v !== r.yieldQuantity) void act(() => rec.updateRecipe(r.id, { yieldQuantity: v }), 'No se pudo guardar.')
                    }}
                  />
                </label>
                <label className="adm-field">
                  <span className="adm-field__label">Unidad de rendimiento (clave SAT)</span>
                  <input
                    className="adm-input"
                    defaultValue={r.yieldUnit ?? 'H87'}
                    disabled={busy}
                    placeholder="H87, KGM, LTR…"
                    onBlur={(e) => {
                      const v = e.target.value.trim()
                      if (v && v !== r.yieldUnit) void act(() => rec.updateRecipe(r.id, { yieldUnit: v }), 'No se pudo guardar.')
                    }}
                  />
                </label>
              </section>

              {/* ── Producto vinculado ───────────────────────────────────── */}
              {/* productId lo acepta RecipeUpdateInput y el cliente updateRecipe ya lo
                  pasa; lo enlazamos directo. El selector reusa el mismo catálogo
                  (listAllProducts) que el de ingredientes. */}
              <section style={{ display: 'grid', gap: 12 }}>
                <SectionTitle>Producto vinculado</SectionTitle>
                <label className="adm-field">
                  <span className="adm-field__label">Producto del menú que produce esta receta</span>
                  <select
                    className="adm-select"
                    value={r.productId ?? ''}
                    disabled={busy}
                    onChange={(e) => act(() => rec.updateRecipe(r.id, { productId: e.target.value || null }), 'No se pudo vincular el producto.')}
                    aria-label="Producto vinculado"
                  >
                    <option value="">— Sin vincular —</option>
                    {pickableGroups.map((g) => (
                      <optgroup key={g.label} label={g.label}>
                        {g.items.map((p) => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` · ${p.sku}` : ''}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </label>
                {r.productId && !productLookup(r.productId) && (
                  <p className="adm-row__meta">Vinculado a <span style={{ fontFamily: 'var(--font-mono)' }}>{r.productId}</span> (no encontrado en el catálogo actual).</p>
                )}
              </section>

              {/* ── Estado ───────────────────────────────────────────────── */}
              {/* active lo acepta RecipeUpdateInput y el cliente updateRecipe ya lo pasa. */}
              <section style={{ display: 'grid', gap: 12 }}>
                <SectionTitle>Estado</SectionTitle>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: busy ? 'default' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={r.active}
                    disabled={busy}
                    onChange={(e) => act(() => rec.updateRecipe(r.id, { active: e.target.checked }), 'No se pudo cambiar el estado.')}
                    aria-label="Receta activa"
                  />
                  <span>
                    <span style={{ fontWeight: 600 }}>Receta activa</span>
                    <span className="adm-row__meta" style={{ display: 'block' }}>Si se desactiva, deja de usarse para producción sin archivarla.</span>
                  </span>
                </label>
              </section>

              {/* ── Imagen ───────────────────────────────────────────────── */}
              {/* imageUrl se LEE del grafo pero RecipeUpdateInput aún no lo acepta:
                  solo-lectura aquí; editarlo está en "flagged" (extender mutation). */}
              <section style={{ display: 'grid', gap: 12 }}>
                <SectionTitle>Imagen</SectionTitle>
                {r.imageUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img
                      src={r.imageUrl}
                      alt={r.name}
                      style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 'var(--radius-md, 12px)', border: '1px solid var(--pos-separator)' }}
                    />
                    <span className="adm-row__meta" style={{ wordBreak: 'break-all', fontFamily: 'var(--font-mono)' }}>{r.imageUrl}</span>
                  </div>
                ) : (
                  <p className="adm-row__meta">Sin imagen. La edición de imagen aún no está disponible en el grafo.</p>
                )}
              </section>

              {/* ── Avanzado ─────────────────────────────────────────────── */}
              {/* yieldPercent se LEE del grafo (lo acepta RecipeUpdateInput) pero
                  ningún cliente exportado lo guarda hoy → solo-lectura + flagged. */}
              <section style={{ display: 'grid', gap: 12 }}>
                <SectionTitle>Avanzado</SectionTitle>
                <ReadOnlyFact label="Rendimiento (%)">{r.yieldPercent != null && r.yieldPercent !== '' ? `${r.yieldPercent}%` : '—'}</ReadOnlyFact>
              </section>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={r.id} />}
        </EntityDetailPage>
      )}

      <Sheet
        open={addingIng}
        title="Agregar ingrediente"
        onClose={() => { setAddingIng(false); setIngError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setAddingIng(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveIngredient} disabled={busy || !ingForm.productId}>{busy ? 'Guardando…' : 'Agregar'}</button>
        </>}
      >
        {ingError && <div className="adm-banner adm-banner--danger" role="alert">{ingError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Insumo o producto</span>
          <EntityCombobox<Product>
            value={ingComboValue}
            onChange={(p) => setIngForm({ ...ingForm, productId: p?.id ?? '', ...(p?.unit ? { unit: p.unit } : {}) })}
            fetchOptions={searchPickable}
            renderOption={(p) => {
              const needs = (p.tags ?? []).includes('needs-attention:cost-0')
              return <span>{p.name}{p.sku ? <span className="adm-muted"> · {p.sku}</span> : null}{needs ? <span style={{ color: 'var(--pos-warning)' }}> · ⚠ sin costo</span> : null}</span>
            }}
            getOptionValue={(p) => p.id}
            getOptionLabel={(p) => p.name}
            placeholder="Buscar insumo o producto…"
            allowClear
          />
        </label>

        {!showNewInsumo ? (
          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => { setShowNewInsumo(true); setNewInsumoErr(null) }}>
            <Plus size={14} /> ¿No está en la lista? Crear insumo nuevo
          </button>
        ) : (
          <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Nuevo insumo</div>
            {newInsumoErr && <div className="adm-banner adm-banner--danger" role="alert">{newInsumoErr}</div>}
            <label className="adm-field">
              <span className="adm-field__label">Nombre del insumo</span>
              <input className="adm-input" value={newInsumo.name} onChange={(e) => setNewInsumo({ ...newInsumo, name: e.target.value })} placeholder="Café en grano, Leche entera…" />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <UnitPicker label="Unidad" value={newInsumo.unit} onChange={(unit) => setNewInsumo({ ...newInsumo, unit })} />
              </div>
              <label className="adm-field" style={{ flex: 1 }}>
                <span className="adm-field__label">Costo por unidad (MXN)</span>
                <input className="adm-input" type="number" min={0} step="0.01" value={newInsumo.costPesos} onChange={(e) => setNewInsumo({ ...newInsumo, costPesos: e.target.value })} placeholder="0.00" />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="adm-btn adm-btn--sm" onClick={() => setShowNewInsumo(false)} disabled={creatingInsumo}>Cancelar</button>
              <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={createNewInsumo} disabled={creatingInsumo || !newInsumo.name.trim()}>{creatingInsumo ? 'Creando…' : 'Crear y usar'}</button>
            </div>
          </div>
        )}

        {pickable.data && pk.length === 0 && (
          <p className="adm-row__meta">No hay productos ni insumos en el catálogo. Crea el primero con el botón de arriba.</p>
        )}

        <label className="adm-field">
          <span className="adm-field__label">Cantidad</span>
          <input className="adm-input" type="number" min={0} step="0.0001" value={ingForm.quantity} onChange={(e) => setIngForm({ ...ingForm, quantity: Number(e.target.value) })} />
        </label>
        <UnitPicker label="Unidad" value={ingForm.unit} onChange={(unit) => setIngForm({ ...ingForm, unit })} />
        <label className="adm-field">
          <span className="adm-field__label">Merma (%)</span>
          <input className="adm-input" type="number" min={0} step="0.01" value={ingForm.wasteFactor} onChange={(e) => setIngForm({ ...ingForm, wasteFactor: Number(e.target.value) })} placeholder="0" />
          <span className="adm-row__meta" style={{ marginTop: 4 }}>Porcentaje de merma esperada (Toast pattern). Ej. 5 = 5%.</span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={ingForm.optional} onChange={(e) => setIngForm({ ...ingForm, optional: e.target.checked })} /> Ingrediente opcional</label>
      </Sheet>

      <Sheet
        open={editingIng != null}
        title="Editar ingrediente"
        onClose={() => { setEditingIng(null); setEditIngError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditingIng(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEditIngredient} disabled={busy || !(editIngForm.quantity > 0) || !editIngForm.unit.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {editIngError && <div className="adm-banner adm-banner--danger" role="alert">{editIngError}</div>}
        {editingIng && <label className="adm-field">
          <span className="adm-field__label">Insumo</span>
          <input className="adm-input" value={productName(editingIng.productId)} disabled aria-readonly="true" />
        </label>}
        <label className="adm-field">
          <span className="adm-field__label">Cantidad</span>
          <input className="adm-input" type="number" min={0} step="0.0001" value={editIngForm.quantity} onChange={(e) => setEditIngForm({ ...editIngForm, quantity: Number(e.target.value) })} />
        </label>
        <UnitPicker label="Unidad" value={editIngForm.unit} onChange={(unit) => setEditIngForm({ ...editIngForm, unit })} />
        <label className="adm-field">
          <span className="adm-field__label">Merma (%)</span>
          <input className="adm-input" type="number" min={0} step="0.01" value={editIngForm.wasteFactor} onChange={(e) => setEditIngForm({ ...editIngForm, wasteFactor: Number(e.target.value) })} placeholder="0" />
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={editIngForm.optional} onChange={(e) => setEditIngForm({ ...editIngForm, optional: e.target.checked })} /> Ingrediente opcional</label>
      </Sheet>

      <Sheet
        open={renaming}
        title="Renombrar receta"
        onClose={() => { setRenaming(false); setRenameError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setRenaming(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveRename} disabled={busy || !renameName.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {renameError && <div className="adm-banner adm-banner--danger" role="alert">{renameError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Nombre</span>
          <input className="adm-input" value={renameName} onChange={(e) => setRenameName(e.target.value)} placeholder="Nombre de la receta" />
        </label>
      </Sheet>

      <Sheet
        open={addingSub}
        title="Agregar sub-receta"
        onClose={() => { setAddingSub(false); setSubError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setAddingSub(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveSubRecipe} disabled={busy || !subForm.subId}>{busy ? 'Guardando…' : 'Agregar'}</button>
        </>}
      >
        {subError && <div className="adm-banner adm-banner--danger" role="alert">{subError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Receta</span>
          <select className="adm-select" value={subForm.subId} onChange={(e) => setSubForm({ ...subForm, subId: e.target.value })} aria-label="Receta hija">
            <option value="">— Selecciona una receta —</option>
            {(allRecipes.data ?? [])
              .filter((other) => r != null && other.id !== r.id && !r.subRecipes.some((s) => s.subId === other.id))
              .map((other) => <option key={other.id} value={other.id}>{other.name}</option>)}
          </select>
          <span className="adm-row__meta" style={{ marginTop: 4 }}>Vincula otra receta como componente. Evita usar la misma receta (ciclo).</span>
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Cantidad</span>
          <input className="adm-input" type="number" min={0} step="0.0001" value={subForm.quantity} onChange={(e) => setSubForm({ ...subForm, quantity: Number(e.target.value) })} />
        </label>
        {allRecipes.data && (allRecipes.data.filter((other) => r != null && other.id !== r.id).length === 0) && (
          <p className="adm-row__meta">No hay otras recetas disponibles para vincular.</p>
        )}
      </Sheet>
    </div>
  )
}

/** Subtítulo discreto de sección — marca NintaiGO: mono, uppercase, terciario. */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 11,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: 'var(--pos-fg-tertiary)',
    }}>{children}</div>
  )
}

/** Dato de solo-lectura (campos que el grafo aún no deja editar). */
function ReadOnlyFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{children}</div>
    </div>
  )
}
